import {
    DAY_INDEX,
    DAY_NAMES,
    DISPLAY_DAYS,
    MINUTES_IN_DAY,
    MINUTES_IN_WEEK,
    RESTAURATION_FLAG,
    SCHEDULE_FIELDS,
} from "../constants/restauration";

/** « 21. La Pool house » → « La Pool house » : le numéro sert à la liste. */
export const cleanPlaceName = (name) =>
    typeof name === "string" ? name.replace(/^\s*\d+\.\s*/, "").trim() : "";

/* Les lieux que Webflow déclare comme espaces de restauration.

   Le booléen est comparé à `true` et non évalué : une chaîne « false » venue du
   CMS est vraie en JavaScript, et afficherait l'amphithéâtre comme restaurant.
   Tout ce qui n'est pas un tableau d'objets est traité comme une liste vide —
   l'API a déjà échoué ailleurs, ce n'est pas au bouton de le signaler. */
export const getRestaurationPlaces = (places) =>
    Array.isArray(places)
        ? places.filter(
              (place) => place != null && place[RESTAURATION_FLAG] === true,
          )
        : [];

/* Le champ CMS est un texte riche : Webflow enveloppe chaque ligne dans un
   paragraphe, il faut donc rendre aux balises leur retour à la ligne avant de
   découper. Un champ texte simple traverse la fonction sans dommage. */
const toLines = (value) =>
    String(value)
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;|&#160;/gi, " ")
        .split(/[\n\r]+/);

/* Le format admis, et lui seul :
 *
 *     Jour:11h00-19h00
 *     Jour:11h00-14h00,19h00-22h30
 *
 * Les minutes sont omissibles (« 11h ») et la casse est libre (« LUNDI »,
 * « lundi »). Rien d'autre n'est accepté : pas d'espace, pas de « à », pas de
 * point-virgule, pas de « 11:00 », pas de tiret long. Une ligne qui s'en écarte
 * est ignorée, donc le jour compte pour fermé.
 */
const DAY_LINE = new RegExp(`^(${Object.keys(DAY_INDEX).join("|")}):(.+)$`);
const RANGE = /^(\d{1,2})h(\d{2})?-(\d{1,2})h(\d{2})?$/;

/** « 11 » + « 30 » → 690. Refuse ce qui n'est pas une heure du jour. */
const toMinutes = (hours, minutes) => {
    const total = Number(hours) * 60 + Number(minutes || 0);
    // « 25h » et « 11h70 » n'existent pas ; 24h00 clôt la journée.
    if (Number(minutes || 0) > 59 || total > MINUTES_IN_DAY) return null;
    return total;
};

/* Une fin antérieure au début franchit minuit : « Vendredi:20h00-02h00 » est
   une soirée du vendredi, pas une erreur de saisie. Le créneau reste attaché au
   jour de son ouverture — c'est ainsi qu'on l'annonce, et c'est ce que le
   lecteur cherche le vendredi soir. `durationOf` fait le calcul. */
const parseRange = (value) => {
    const match = RANGE.exec(value);
    if (!match) return null;

    const start = toMinutes(match[1], match[2]);
    const end = toMinutes(match[3], match[4]);
    if (start === null || end === null) return null;
    // Un début à 24h00 n'existe pas, et « 20h00-20h00 » est trop ambigu pour
    // être deviné : la journée entière s'écrit « 00h00-24h00 ».
    if (start >= MINUTES_IN_DAY || end === start) return null;
    return { start, end };
};

/** Longueur du créneau, minuit franchi ou non : 20h00-02h00 dure 6 heures. */
const durationOf = ({ start, end }) =>
    end > start ? end - start : end + MINUTES_IN_DAY - start;

/**
 * « Lundi:11h00-14h00,19h00-22h30 » → `{ 1: [{ start: 660, end: 840 }, …] }`,
 * en minutes depuis minuit et en jours indexés comme `Date.getDay()`. Les
 * bornes sont gardées telles quelles : un `end` inférieur au `start` signale
 * une fermeture le lendemain.
 *
 * Toute ligne hors format est ignorée en silence : « Fermé », une coquille ou
 * du texte libre laissent simplement le jour absent — c'est exactement le
 * résultat voulu, et mieux vaut un jour manquant qu'une heure devinée.
 */
export const parseSchedule = (raw) => {
    const week = {};
    if (!raw) return week;

    toLines(raw).forEach((line) => {
        // Seule la casse est absorbée ici ; le reste doit être exact.
        const match = DAY_LINE.exec(line.toLowerCase());
        if (!match) return;

        /* Une seule plage illisible condamne toute la ligne. Garder les autres
           serait le pire des comportements : « Lundi:11h00-14h00, 19h00-22h30 »
           (une espace après la virgule) afficherait le midi, perdrait le soir,
           et personne ne verrait passer l'erreur. */
        const ranges = match[2].split(",").map(parseRange);
        if (ranges.some((range) => range === null)) return;

        // Un jour écrit deux fois complète la journée au lieu de l'écraser :
        // perdre une ligne saisie serait le pire des comportements.
        const day = DAY_INDEX[match[1]];
        week[day] = (week[day] || [])
            .concat(ranges)
            .sort((a, b) => a.start - b.start);
    });

    return week;
};

/** La grille d'un lieu, prise dans le premier champ CMS qui en contient une. */
export const getPlaceSchedule = (place) => {
    if (place == null) return {};
    for (const field of SCHEDULE_FIELDS) {
        const week = parseSchedule(place[field]);
        if (Object.keys(week).length !== 0) return week;
    }
    return {};
};

/** 690 → « 11h30 », 120 → « 02h00 » : la forme lue sur la fiche. */
export const formatTime = (minutes) =>
    `${String(Math.floor(minutes / 60)).padStart(2, "0")}h` +
    `${String(minutes % 60).padStart(2, "0")}`;

/** « 11h00-19h00, 21h00-02h00 » : la journée d'un lieu, d'un seul tenant. */
const formatRanges = (ranges) =>
    ranges
        .map(({ start, end }) => `${formatTime(start)}-${formatTime(end)}`)
        .join(", ");

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * La grille prête à afficher, une ligne par groupe de jours consécutifs qui
 * partagent les mêmes horaires :
 *
 *   [{ days: "Lundi, Mardi, Mercredi", hours: "11h00-19h00" },
 *    { days: "Jeudi", hours: "11h00-19h00, 21h00-02h00" }]
 *
 * Les jours sans horaire ne sont pas listés : ils sont fermés, et l'encadré
 * gagne à ne montrer que ce qui ouvre. Ils coupent en revanche le groupe, pour
 * que la semaine se lise dans l'ordre du lundi au dimanche — deux jours ne se
 * retrouvent jamais sur la même ligne s'ils ne se suivent pas.
 */
export const formatSchedule = (schedule = {}) => {
    const groups = [];

    DISPLAY_DAYS.forEach((day, position) => {
        const ranges = schedule[day];
        if (!ranges) return;

        const hours = formatRanges(ranges);
        const previous = groups[groups.length - 1];

        if (previous && previous.hours === hours && previous.position === position - 1) {
            previous.days.push(capitalize(DAY_NAMES[day]));
            previous.position = position;
            return;
        }

        groups.push({ days: [capitalize(DAY_NAMES[day])], hours, position });
    });

    return groups.map(({ days, hours }) => ({ days: days.join(", "), hours }));
};

const weekMinute = (date) =>
    date.getDay() * MINUTES_IN_DAY + date.getHours() * 60 + date.getMinutes();

/**
 * L'espace vers lequel envoyer l'utilisateur : celui qui sert à cet instant,
 * sinon le prochain à ouvrir — à 2h du matin, l'information utile reste « la
 * Pool house ouvre à 11h30 », pas un bouton grisé.
 *
 * Renvoie `null` si aucun espace de restauration n'a d'horaire lisible :
 * l'appelant n'affiche alors pas le bouton.
 *
 * Le campus n'ouvre jamais deux espaces en même temps ; si cela arrivait, le
 * premier de la liste triée gagne, pour que le bouton reste prévisible.
 */
export const getRestaurationTarget = (places, now = new Date()) => {
    if (!(now instanceof Date) || Number.isNaN(now.getTime())) return null;

    const current = weekMinute(now);
    let best = null;

    getRestaurationPlaces(places).forEach((place) => {
        const week = getPlaceSchedule(place);

        Object.keys(week).forEach((key) => {
            const day = Number(key);

            week[day].forEach((range) => {
                const start = day * MINUTES_IN_DAY + range.start;
                /* Tout se compte en avant, modulo la semaine : un créneau déjà
                   passé revient sept jours plus tard, jamais avec un délai
                   négatif, et le samedi 20h-02h se prolonge naturellement sur
                   le dimanche matin sans déborder de la semaine. */
                const elapsed = (current - start + MINUTES_IN_WEEK) % MINUTES_IN_WEEK;
                const opensIn = (start - current + MINUTES_IN_WEEK) % MINUTES_IN_WEEK;
                const isOpen = elapsed < durationOf(range);

                // Ouvert maintenant l'emporte sur n'importe quelle attente.
                const rank = isOpen ? -1 : opensIn;
                if (best !== null && rank >= best.rank) return;

                best = {
                    rank,
                    place,
                    mapId: place["map-id"],
                    name: cleanPlaceName(place.name),
                    isOpen,
                    day,
                    dayLabel: DAY_NAMES[day],
                    opensAt: formatTime(range.start),
                    closesAt: formatTime(range.end),
                    // La fermeture tombe le lendemain : « jusqu'à 2h » se dit
                    // autrement que « jusqu'à 14h ».
                    overnight: range.end <= range.start,
                };
            });
        });
    });

    if (best === null) return null;

    const { rank, ...target } = best;
    return target;
};

/** Y a-t-il de quoi proposer le bouton ? */
export const hasRestauration = (places) =>
    getRestaurationPlaces(places).some(
        (place) => Object.keys(getPlaceSchedule(place)).length !== 0,
    );
